import React from 'react';
import autobind from 'core-decorators/lib/autobind';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash/get';
import VideoPlayer from 'widget/player-widget/components/new-player/player';
import bi from 'shared/bi/bi';
import { VODBiEvents } from 'shared/utils/wix-connect';
import { getBuyButtonDisplayType } from '@wix/wix-vod-shared/src/common/utils/overlay-buttons-utils';
import { canAccessFullVideo } from '@wix/wix-vod-shared/src/widget/ui-selectors/video-access';
import { convertSToMS } from '@wix/wix-vod-shared/src/widget/utils/time';
import { isScheduledLive } from '@wix/wix-vod-shared/src/common/ui-selectors/video/live';
import { isExternalVideo, isLiveVideo } from '@wix/wix-vod-shared/src/common/ui-selectors/video/video-origin';
import getVideoUrls from 'widget/selectors/get-video-urls';
import { getAllSettings } from 'shared/selectors/app-settings';
import { getCurrentSiteUser } from 'widget/selectors/current-site-user';

import fetchVideoUrls from 'widget/redux/client/actions/video-urls/fetch';
import reportStats from 'widget/redux/server/actions/stats/report';

import fetchVideoCards from 'widget/redux/server/actions/video-cards/get';
import { cleanupVideoCards } from 'widget/redux/client/actions/video-cards/cleanup';
import { isVideoCardsEnabled } from 'widget/selectors/video-cards';

import { isVideoOverQuota } from '@wix/wix-vod-shared/src/common/ui-selectors/video/quota';
import { isOverQuotaStateEnabled } from 'shared/selectors/experiments';

import STATS_EVENTS from 'shared/constants/stats/events';
import translations from '@wix/wix-vod-shared/src/common/i18n/translations';

import { ERRORS } from 'playable/dist/src/constants';


/*
 * PlayerWrapper does these while wrapping VODPlayer:
 * - sends BI
 * - logs statistics
 * - checks memberOnly setting
 */
@connect((state, props) => ({
  videoUrls: getVideoUrls(state, props),
  appSettings: getAllSettings(state),
  currentSiteUser: getCurrentSiteUser(state),
  isVideoCardsEnabled: isVideoCardsEnabled(state, props),
  isOverQuota: isVideoOverQuota(get(props, 'videoItem', {}), isOverQuotaStateEnabled(state)),
  isExternal: isExternalVideo(get(props, 'videoItem', {})),
  isScheduled: isScheduledLive(get(props, 'videoItem', {})),
  isLive: isLiveVideo(get(props, 'videoItem', {})),
}), {
  reportStats,
  fetchVideoUrls,
  fetchVideoCards,
  cleanupVideoCards,
})
@VODBiEvents()
export default class PlayerWithStatAndBI extends React.Component {

  static propTypes = {
    width: PropTypes.number,
    height: PropTypes.number,
    videoUrls: PropTypes.object,
    videoItem: PropTypes.object,
    //remove after new player
    channelData: PropTypes.object,
    size: PropTypes.object,
    simple: PropTypes.bool,
    mobileMode: PropTypes.bool,
    overlay: PropTypes.any,
    onFullScreenChanged: PropTypes.func,
    openPreviewPrompt: PropTypes.func,
    onPlayStart: PropTypes.func,
    onEnded: PropTypes.func,
    preload: PropTypes.string,
    reportStats: PropTypes.func,
    fetchVideoUrls: PropTypes.func,
    biEvent: PropTypes.func,
    appSettings: PropTypes.object,
    canShowChannelCover: PropTypes.bool,
    mainOrThumbnailLocation: PropTypes.string,
    muted: PropTypes.bool,
    loop: PropTypes.bool,
    showInitialOverlay: PropTypes.bool,
    currentSiteUser: PropTypes.object,
    memberOnlyOverlay: PropTypes.node,
    onMemberSignUp: PropTypes.func,
    currentTime: PropTypes.number,

    onSubscribeClick: PropTypes.func,
    onPurchaseClick: PropTypes.func,
    isVideoCardsEnabled: PropTypes.bool,
    fetchVideoCards: PropTypes.func,
    onPlayRequest: PropTypes.func,
    shouldLoadVideo: PropTypes.bool,
    isOverQuota: PropTypes.bool,
    isExternal: PropTypes.bool,
    isScheduled: PropTypes.bool,
    isLive: PropTypes.bool,
    cleanupVideoCards: PropTypes.func,
  };

  static defaultProps = {
    openPreviewPrompt: _.noop,
    onFullScreenChanged: _.noop,
    onPlayStart: _.noop,
    onEnded: _.noop,
    onMemberSignUp: _.noop,
    onSubscribeClick: _.noop,
    onPurchaseClick: _.noop,
    mainOrThumbnailLocation: 'main',
    simple: false,
    mobileMode: false,
    onPlayRequest: _.noop,
    shouldLoadVideo: true,
    isLive: false,
  };

  componentDidMount() {
    this.checkVideoUrls(this.props);
    this.updateVideoCards(this.props.videoItem);
  }

  componentWillReceiveProps(nextProps) {
    this.checkVideoUrls(nextProps);
  }

  componentDidUpdate(prevProps) {
    const isVideoCardsEnabledChanged = !prevProps.isVideoCardsEnabled && this.props.isVideoCardsEnabled;
    const isVideoIdChanged = prevProps.videoItem.id !== this.props.videoItem.id;

    if (isVideoIdChanged || isVideoCardsEnabledChanged) {
      this.updateVideoCards(this.props.videoItem);
    }
  }

  reloadCounts = {};

  checkVideoUrls({ fetchVideoUrls, videoItem, videoUrls, shouldLoadVideo }) {
    if (!shouldLoadVideo) {
      return;
    }

    const { isExternal, isScheduled } = this.props;

    // TODO: Need to move this logic out of view
    // See the same in SlidableListWithLazyLoad
    if (!videoUrls && !isExternal && !isScheduled) {
      if (!videoItem.memberOnly || this.isUserLogged) {
        fetchVideoUrls(videoItem);
      }
    }
  }

  updateVideoCards(videoItem) {
    const { isVideoCardsEnabled, fetchVideoCards, shouldLoadVideo, cleanupVideoCards } = this.props;

    if (!shouldLoadVideo || !videoItem) {
      return;
    }

    if (!isVideoCardsEnabled || !videoItem.hasCards) {
      cleanupVideoCards();
    } else {
      fetchVideoCards(videoItem.listId, videoItem.id);
    }
  }

  get isUserLogged() {
    const { currentSiteUser } = this.props;

    return Boolean(currentSiteUser);
  }

  @autobind
  reloadVideoUrls(error) {
    if (!error) {
      return;
    }

    const { fetchVideoUrls, videoItem } = this.props;
    const { id } = videoItem;

    this.reloadCounts[id] = this.reloadCounts[id] || 0;
    this.reloadCounts[id] += 1;

    if (this.reloadCounts[id] > 1) {
      return;
    }

    switch (error.errorType) {
      case ERRORS.MANIFEST_LOAD:
      case ERRORS.LEVEL_LOAD:
      case ERRORS.CONTENT_LOAD:
        fetchVideoUrls(videoItem, true);
        break;
      default:
        break;
    }
  }

  @autobind
  onPlayStart() {
    this.props.onPlayStart();
    this.sendVidStartPlayBI();
  }

  @autobind
  onEnded() {
    this.sendVidPlayedBI();

    const { onEnded, reportStats, channelData, videoItem, currentSiteUser } = this.props;
    onEnded();

    let event;
    if (canAccessFullVideo(channelData, videoItem, currentSiteUser)) {
      event = STATS_EVENTS.VIDEO_PLAY_COMPLETE;
    } else {
      event = STATS_EVENTS.VIDEO_PLAY_TRAILER;
    }

    reportStats(event, {
      channel: channelData,
      videoItem,
    });
  }

  sendVidStartPlayBI() {
    const { mainOrThumbnailLocation: button, channelData, videoItem: { id, durationSec, videoSource } } = this.props;

    bi.log('widget.vid.start.play', {
      channelID: channelData.id,
      videoID: id,
      button,
      duration: convertSToMS(durationSec),
      videoType: videoSource,
    });
  }

  sendVidPlayedBI() {
    const { mainOrThumbnailLocation: button, channelData, videoItem: { id, durationSec }, isLive } = this.props;

    bi.log('widget.vid.played', {
      channelID: channelData.id,
      videoID: id,
      button,
      isLive,
      duration: convertSToMS(durationSec),
    });
  }

  @autobind
  sendBIForVideoInfoShow() {
    this.sendBI('widget.vid.info.clicked');
  }

  @autobind
  sendBIForPurchaseRequested() {
    this.sendBuyBI('widget.vid.buy.clicked');
  }

  @autobind
  sendBIForShareRequested(networkName) {
    const { reportStats, channelData, videoItem } = this.props;

    reportStats(STATS_EVENTS.VIDEO_SHARE, {
      channel: channelData,
      videoItem,
      network: {
        name: networkName,
      },
    });

    this.sendBI('widget.vid.share.clicked', {
      button: networkName,
    });
  }

  sendBI(name, params) {
    const { channelData, videoItem, biEvent } = this.props;
    const { id, videoSource } = videoItem;

    biEvent(name, _.assign({}, params, {
      channelID: channelData.id,
      videoID: id,
      videoType: videoSource,
    }));
  }

  sendBuyBI(name) {
    const { videoItem, channelData, mainOrThumbnailLocation: button } = this.props;

    const buyType = getBuyButtonDisplayType(channelData, videoItem);

    this.sendBI(name, {
      buyType,
      button,
    });
  }

  @autobind
  sendBIForFullScreenChanged(isFullScreen) {
    const { channelData, videoItem } = this.props;

    if (isFullScreen) {
      bi.log('widget.vid.fullScreen.entered', {
        channelID: channelData.id,
        videoID: videoItem.id,
        origin: 'on_site',
      });
    }

    this.props.onFullScreenChanged(isFullScreen);
  }

  @autobind
  onPurchaseRequest() {
    const { onPurchaseClick } = this.props;

    this.sendBIForPurchaseRequested();
    onPurchaseClick();
  }

  @autobind
  onSubscribeRequest() {
    const { onSubscribeClick } = this.props;

    this.sendBIForPurchaseRequested();
    onSubscribeClick();
  }

  render() {
    const {
      size,
      channelData,
      videoItem,
      memberOnlyOverlay,
      canShowChannelCover,
      width,
      height,
      appSettings,
      onMemberSignUp,
      isOverQuota,
    } = this.props;

    const props = _.omit(
      this.props,
      'size',
      'reportStats',
    );

    if (!videoItem.memberOnly || this.isUserLogged) {
      return (
        <VideoPlayer {...props}
                     {...size}
                     isFetchDisabled
                     translations={translations.getAll()}
                     onPlayStart={this.onPlayStart}
                     onEnded={this.onEnded}
                     onError={this.reloadVideoUrls}
                     onPurchaseRequest={this.onPurchaseRequest}
                     onSubscribeRequest={this.onSubscribeRequest}
                     onShareRequest={this.sendBIForShareRequested}
                     onVideoInfoShow={this.sendBIForVideoInfoShow}
                     onFullScreenChanged={this.sendBIForFullScreenChanged}
                     isOverQuota={isOverQuota} />
      );
    }

    return React.cloneElement(memberOnlyOverlay, {
      showChannelCover: canShowChannelCover,
      videoItem,
      width,
      height,
      appSettings,
      channelData,
      onPlayClick: onMemberSignUp,
    });
  }
}



// WEBPACK FOOTER //
// ./src/widget/components/players/player-wrapper.js